import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { UserProvider } from './context/UserContext';
import { initializeFakeData } from './services/api';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import RoleSelectionPage from './pages/RoleSelectionPage';
import DashboardPage from './pages/DashboardPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminManagement from './pages/AdminManagement';
import JobPostManagement from './pages/JobPostManagement';
import UsersManagement from './pages/UsersManagement';
import JobSeekerProfilePage from './pages/JobSeekerProfilePage';
import EmployerProfilePage from './pages/EmployerProfilePage';
import CompanyProfilePage from './pages/CompanyProfilePage';
import CandidateProfilePage from './pages/CandidateProfilePage';
import ReviewPage from './pages/ReviewPage';
import AIExplanationPage from './pages/AIExplanationPage';
import SettingsPage from './pages/SettingsPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import ContactPage from './pages/ContactPage';
import AboutPage from './pages/AboutPage';
import CompanyReviews from './pages/CompanyReviews';
import JobDetailsPage from './pages/JobDetailsPage';
import JobPostingPage from './pages/JobPostingPage';

function App() {
  useEffect(() => {
    // Initialize fake data on app load
    initializeFakeData();
  }, []);

  return (
    <Router>
      <AuthProvider>
        <UserProvider>
          <div className="flex flex-col min-h-screen">
            <Navbar />
            <main className="flex-grow">
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/company-reviews" element={<CompanyReviews />} />

                {/* Role Selection - Semi-protected (requires auth but no role) */}
                <Route
                  path="/select-role"
                  element={
                    <ProtectedRoute>
                      <RoleSelectionPage />
                    </ProtectedRoute>
                  }
                />

                {/* Protected Routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />

                {/* Admin Dashboard */}
                <Route
                  path="/admin/dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/job-management"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <JobPostManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <UsersManagement />
                    </ProtectedRoute>
                  }
                />

                {/* Profile Routes */}
                <Route
                  path="/profile/jobseeker"
                  element={
                    <ProtectedRoute allowedRoles={['jobseeker']}>
                      <JobSeekerProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile/employer"
                  element={
                    <ProtectedRoute allowedRoles={['employer']}>
                      <EmployerProfilePage />
                    </ProtectedRoute>
                  }
                />

                {/* Company and Candidate Profile Pages */}
                <Route
                  path="/company/:companyId"
                  element={
                    <ProtectedRoute>
                      <CompanyProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/candidate/:candidateId"
                  element={
                    <ProtectedRoute>
                      <CandidateProfilePage />
                    </ProtectedRoute>
                  }
                />

                {/* Review Page */}
                <Route
                  path="/reviews/:targetId"
                  element={
                    <ProtectedRoute>
                      <ReviewPage />
                    </ProtectedRoute>
                  }
                />

                {/* Job Details Page */}
                <Route
                  path="/job/:jobId"
                  element={
                    <ProtectedRoute allowedRoles={['jobseeker']}>
                      <JobDetailsPage />
                    </ProtectedRoute>
                  }
                />

                {/* Job Posting Page */}
                <Route
                  path="/post-job"
                  element={
                    <ProtectedRoute allowedRoles={['employer']}>
                      <JobPostingPage />
                    </ProtectedRoute>
                  }
                />

                {/* AI Match Explanation */}
                <Route
                  path="/ai-match/:matchId"
                  element={
                    <ProtectedRoute allowedRoles={['jobseeker']}>
                      <AIExplanationPage />
                    </ProtectedRoute>
                  }
                />

                {/* Settings Routes */}
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/settings"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <AdminSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/manage"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <AdminManagement />
                    </ProtectedRoute>
                  }
                />

                {/* Fallback Route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </main>
            <Footer />
          </div>
        </UserProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
